import { useEffect, useRef } from 'react';

/**
 * useGlobalShortcuts — registra atalhos globais do desktop OS.
 *
 * Atalhos:
 *   Cmd/Ctrl + K  → toggle command palette
 *   Cmd/Ctrl + M  → toggle wallpaper (matrix ↔ grid)
 *   Cmd/Ctrl + L  → toggle lofi player
 *   Cmd/Ctrl + `  → toggle terminal
 *   Esc           → close paleta / fecha janela em foco se paleta fechada
 *
 * Callbacks armazenados em ref → useEffect só re-fira em `paletteOpen`.
 * Sem ref pattern, inline arrows no caller causariam React #185 (loop).
 *
 * @param {Object} opts
 * @param {() => void} opts.onTogglePalette
 * @param {() => void} opts.onToggleWallpaper
 * @param {() => void} opts.onToggleLofi
 * @param {() => void} opts.onToggleTerminal
 * @param {boolean}   opts.paletteOpen
 * @param {() => void} opts.onEscapePressed
 */
export function useGlobalShortcuts({
  onTogglePalette,
  onToggleWallpaper,
  onToggleLofi,
  onToggleTerminal,
  onEscapePressed,
  paletteOpen = false,
}) {
  const cbRef = useRef({
    onTogglePalette,
    onToggleWallpaper,
    onToggleLofi,
    onToggleTerminal,
    onEscapePressed,
    paletteOpen,
  });
  cbRef.current = {
    onTogglePalette,
    onToggleWallpaper,
    onToggleLofi,
    onToggleTerminal,
    onEscapePressed,
    paletteOpen,
  };

  useEffect(() => {
    const isEditable = (el) => {
      if (!el) return false;
      const tag = el.tagName;
      return tag === 'INPUT' || tag === 'TEXTAREA' || el.isContentEditable;
    };

    const handler = (e) => {
      const cbs = cbRef.current;
      const mod = e.metaKey || e.ctrlKey;

      if (e.key === 'Escape') {
        if (cbs.paletteOpen) return;
        if (cbs.onEscapePressed) {
          e.preventDefault();
          cbs.onEscapePressed();
        }
        return;
      }

      if (!mod) return;
      if (e.shiftKey) return;

      const key = e.key.toLowerCase();

      if (key === 'k') {
        e.preventDefault();
        cbs.onTogglePalette && cbs.onTogglePalette();
        return;
      }

      if (isEditable(e.target)) return;

      if (key === 'm') {
        e.preventDefault();
        cbs.onToggleWallpaper && cbs.onToggleWallpaper();
        return;
      }

      if (key === 'l') {
        e.preventDefault();
        cbs.onToggleLofi && cbs.onToggleLofi();
        return;
      }

      if (e.key === '`' || key === '~' || e.code === 'Backquote') {
        e.preventDefault();
        cbs.onToggleTerminal && cbs.onToggleTerminal();
        return;
      }
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);
}

/**
 * useFocusTrap — trap focus dentro de um container (modais, palettes).
 * Tab/Shift+Tab cicla entre focusables. Click fora → onOutsideClick.
 * Esc não tratado aqui — caller decide.
 *
 * @param {Object} opts
 * @param {React.RefObject<HTMLElement>} opts.containerRef
 * @param {boolean} opts.active
 * @param {() => void} [opts.onOutsideClick]
 */
export function useFocusTrap({ containerRef, active, onOutsideClick }) {
  useEffect(() => {
    if (!active) return;
    const container = containerRef.current;
    if (!container) return;

    const FOCUSABLE_SEL =
      'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

    const focusables = () =>
      Array.from(container.querySelectorAll(FOCUSABLE_SEL)).filter(
        (el) => !el.hasAttribute('aria-hidden') && el.offsetParent !== null
      );

    // Foco inicial
    const initial = document.activeElement;
    const list = focusables();
    if (list.length > 0) {
      list[0].focus();
    } else {
      container.setAttribute('tabindex', '-1');
      container.focus();
    }

    const onKeyDown = (e) => {
      if (e.key !== 'Tab') return;
      const list = focusables();
      if (list.length === 0) {
        e.preventDefault();
        return;
      }
      const first = list[0];
      const last = list[list.length - 1];
      const active = document.activeElement;
      if (e.shiftKey) {
        if (active === first || !container.contains(active)) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    const onMouseDown = (e) => {
      if (onOutsideClick && !container.contains(e.target)) {
        onOutsideClick();
      }
    };

    container.addEventListener('keydown', onKeyDown);
    // capture phase — outside click antes de quem quer que seja
    document.addEventListener('mousedown', onMouseDown, true);

    return () => {
      container.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onMouseDown, true);
      if (initial && typeof initial.focus === 'function') {
        initial.focus?.();
      }
    };
  }, [active, containerRef, onOutsideClick]);
}

/**
 * usePrefersReducedMotion — detecta prefers-reduced-motion.
 */
export function usePrefersReducedMotion() {
  if (typeof window === 'undefined') return false;
  const mql = window.matchMedia('(prefers-reduced-motion: reduce)');
  return mql.matches;
}
